import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import ErrorList from 'Components/UI/ErrorList';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useEventTracking from 'EventTracking/useEventTrackingLogEvent';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog/Dialog';
import { noop } from 'Helpers/PropTypes';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import MetaControl from 'Components/UI/Meta/MetaControl';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import useUserContext from 'Context/UserContext';
import RichTextEditorCustomContextMenu from 'Components/UI/RichTextEditorCustomContextMenu';
import RichTextEditor from 'Components/UI/RichTextEditor';
import TextField from 'Components/UI/TextField';
import { createInputBaseStyles } from 'Components/UI/styles';
import { pickFunction } from 'Helpers/Affordances';
import fr from 'facet-rest';
import MessageInputActions from './MessageInputActions';
import MessageAttachments from '../MessageAttachments';

const styles = theme => ({
  root: {},
  subjectLine: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  subjectInputBase: {
    ...createInputBaseStyles(theme),
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightBold,
    paddingRight: theme.spacing(1),
    paddingBottom: 0,
    '& input': {
      padding: theme.spacing(1),
    },
    '& label': {
      transform: 'translate(14px, 10px) scale(1)',
    },
  },
  inputRoot: {
    position: 'relative',
  },
  errorList: {
    transition: theme.transitions.create(['transform', 'opacity']),
    opacity: 1,
    marginBottom: theme.spacing(2),
  },
  errorListNoErrors: {
    transform: 'scaleY(0)',
    opacity: 0,
    marginBottom: 0,
  },
  attachments: {
    padding: theme.spacing(1, 0),
  },
  attachmentsNone: {
    padding: theme.spacing(0),
  },
  submitButton: {
    marginLeft: theme.spacing(2),
  },
  templateOptionsMenu: {
    paddingRight: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRight: `1px solid ${theme.palette.facet.lightGray}`,
  },
});

// This component is responsible for passing props from the input hook into appropriate children
const MessageInput = ({
  className,
  classes,
  errors,
  messageResource,
  onUpdateShouldNotifyPlanner,
  showSubject,
  setSubject,
  setTemplateId,
  subject,
  setText,
  text,
  onSubmit,
  onAdditionalButtonClick,
  showAttachmentsButton,
  onAttachmentDelete,
  onAttachmentUploadStart,
  onAttachmentUploadSuccess,
  onAttachmentUploadFailure,
  setUploadProgress,
  uploadProgress,
  MessageInputActionsProps,
  RichTextEditorProps,
  SubmitButtonProps,
  AdditionalButtonProps,
  needsConfirmOnSubmit,
  onConfirm,
  resolveConfirmation,
  confirmationTitle,
  confirmationText,
  confirmationAlertMessage,
  confirmationButtonLabel,
  confirmationOnSubmit,
  confirmationOnCancel,
  confirmationOnClose,
  makeCancelDefaultAction,
  cancelButtonLabel,
  AttachmentUploadButtonProps,
  ConfirmationDialogComponent,
  disableSubmit,
  TemplateOptionsMenuProps,
  messageTemplateCollection,
  saveMessageTemplate,
  removeMessageTemplate,
  overwriteMessageTemplate,
  showAdditionalButton,
  showTemplateOptionsMenu,
  readonly,
  showTemplateTokens,
  setMessageRecipients,
  ...rest
}) => {
  const { logEvent } = useEventTracking();
  const [currentText, setCurrentText] = useState(text);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [requireConfirmation, setRequireConfirmation] = useState(needsConfirmOnSubmit || (messageResource && !messageResource?.meta().field('shouldNotifyPlanner').attr('readonly')));
  const editorRef = useRef();
  const { isAgent, userResource } = useUserContext();
  const messageRecipients = messageResource?.attr('messageRecipients');

  const hasExternalParticipants = (selected) => {
    const options = messageResource?.meta().field('messageRecipients').options().list();
    for (let index = 0; index < selected.length; index++) {
      const isExternal = !options?.find(v => v.attr('id') === selected[index]); // eslint-disable-line
      if (isExternal) {
        return true;
      }
    }
    return false;
  };

  const checkForPromptMatch = (selected) => {
    const options = messageResource?.meta().field('messageRecipients').options().list();
    let returnVal = selected;
    options?.forEach((option) => {
      if (option.attr('prompt') === selected) { returnVal = option.attr('id'); }
    });
    if (messageRecipients?.includes(returnVal)) {
      returnVal = null;
    }
    return returnVal;
  };


  const addExtraParticipant = () => {
    const extraParticipant = document.querySelector('[id*=-picklist]')?.value;
    let newExtraParticipantVal;
    if (extraParticipant && typeof extraParticipant === 'string') {
      newExtraParticipantVal = checkForPromptMatch(extraParticipant);
    }
    if (newExtraParticipantVal) {
      messageRecipients.push(newExtraParticipantVal);
      setMessageRecipients(messageRecipients);
    }
    if (newExtraParticipantVal && hasExternalParticipants(newExtraParticipantVal) && !requireConfirmation) {
      setRequireConfirmation(true);
    }
  };

  if (!needsConfirmOnSubmit && messageResource && !messageResource?.meta().field('shouldNotifyPlanner').attr('readonly')) {
    // eslint-disable-next-line no-param-reassign
    confirmationTitle = 'Should the planner be notified of this message?';
    // eslint-disable-next-line no-param-reassign
    confirmationText = '';
  }

  const resetInput = () => {
    // We are using CKeditor as an uncontrolled input and clearing the text on submit by alternating between
    // an empty string and null
    if (currentText === null || currentText === undefined) {
      setCurrentText('');
    } else {
      setCurrentText(null);
    }
  };

  const handleOnAdditionalButtonClick = async (args = {}) => {
    const { onSuccess, onFailure } = args;
    await onAdditionalButtonClick({
      onSuccess: () => {
        if (onSuccess) onSuccess();
      },
      onFailure: () => {
        if (onFailure) onFailure();
      },
    });
  };

  const handleOnSubmit = async (args = {}) => {
    const { onSuccess, onFailure } = args;
    logEvent('FacetBook: Clicked Submit On A Thread Message Input');
    await onSubmit({
      onSuccess: () => {
        resetInput();
        if (onSuccess) onSuccess();
      },
      onFailure: () => {
        if (onFailure) onFailure();
      },
    });
  };

  const appendMessageTemplate = (selectedTemplate) => {
    if (!editorRef?.current?.editor) {
      return;
    }
    editorRef.current.editor.insertHtml(selectedTemplate.attr('content'));
    setSubject(selectedTemplate.attr('subject'));
    setTemplateId(selectedTemplate.id());
  };

  const appendTemplateTok = (tokenContent) => {
    if (!editorRef?.current?.editor) {
      return;
    }
    editorRef.current.editor.insertHtml(tokenContent);
  };

  const attachmentResources = (messageResource?.attr('attachments')) ? messageResource?.attr('attachments').resources().list() : [];

  const {
    customConfig: rtePropsCustomConfig,
    ...restRichTextEditorProps
  } = RichTextEditorProps;

  const handleOnConfirm = () => {
    setShowConfirm(true);
    if (onConfirm) onConfirm();
  };

  const handleTextEditorChange = (value) => {
    addExtraParticipant();
    if (value === '') resetInput(); // <-- Handles resetting the input upon clearing contents (corrects bug when removing / adding custom templates)
    setText(value);
  };

  const handleSubjectChange = (e) => {
    addExtraParticipant();
    setSubject(e.target.value);
  };

  const editorConfigEmoji = {
    extraPlugins: 'autogrow,editorplaceholder,emoji',
    toolbarLocation: 'bottom',
    toolbarGroups: [
      { name: 'document', groups: ['mode', 'document', 'doctools'] },
      { name: 'forms' },
      { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
      {
        name: 'paragraph',
        groups: ['list', 'bidi'],
      },
      { name: 'links' },
      { name: 'styles' },
      { name: 'tools' },
      { name: 'insert' },
    ],
    removePlugins:
        'elementspath,scayt,contextmenu,liststyle,tabletools,tableselection,stylescombo,format,font,justify',
    autoGrow_onStartup: false,
    ...rtePropsCustomConfig,
  };

  const contextEditorConfig = {
    extraPlugins: 'autogrow,editorplaceholder,emoji',
    toolbarLocation: 'bottom',
    toolbarGroups: [
      { name: 'document', groups: ['mode', 'document', 'doctools'] },
      { name: 'forms' },
      { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
      {
        name: 'paragraph',
        groups: ['list', 'bidi'],
      },
      { name: 'links' },
      { name: 'styles' },
      { name: 'tools' },
      { name: 'insert' },
    ],
    removePlugins:
        'elementspath,scayt,liststyle,tabletools,tableselection,stylescombo,format,font,justify',
    autoGrow_onStartup: false,
    ...rtePropsCustomConfig,
  };

  let pickedTextEditorComponent = pickFunction(userResource, {
    [fr.relationships.default.name]: () => (
      <RichTextEditorCustomContextMenu
        value={currentText}
        onChange={handleTextEditorChange}
        customConfig={contextEditorConfig}
        ref={editorRef}
        {...restRichTextEditorProps}
      />
    ),
  });

  if (!isAgent) {
    pickedTextEditorComponent = pickFunction(userResource, {
      [fr.relationships.default.name]: () => (
        <RichTextEditor
          value={currentText}
          onChange={handleTextEditorChange}
          customConfig={editorConfigEmoji}
          ref={editorRef}
          {...restRichTextEditorProps}
        />
      ),
    });
  }

  const confirmOnCancel = confirmationOnCancel
    ? () => confirmationOnCancel(setCanceling, setShowConfirm, resetInput)
    : (() => {
      setShowConfirm(false);
    });
  const confirmOnClose = () => confirmationOnClose(setCanceling, setShowConfirm, resetInput) || confirmOnCancel;
  return (
    <Box
      className={classNames(classes.root, className)}
      {...rest}
    >
      {showConfirm && (
        <ConfirmationDialog
          title={confirmationTitle}
          message={confirmationText}
          confirmLabel={confirmationButtonLabel}
          cancelLabel={cancelButtonLabel}
          alertMessage={confirmationAlertMessage}
          loading={confirming}
          loadingCancelButton={canceling}
          makeCancelDefaultAction={makeCancelDefaultAction}
          onConfirm={confirmationOnSubmit
            ? async () => {
              await confirmationOnSubmit(setConfirming, setShowConfirm, resetInput);
            }
            : (async () => {
              setConfirming(true);
              try {
                await handleOnSubmit();
                resolveConfirmation(); // This resolves the promise that the submit button is watching to show correct saving state
                setShowConfirm(false);
              } catch (err) {
                displayErrorMessage(err, err.errors);
              }
              setConfirming(false);
            })}
          onCancel={confirmOnCancel}
          onClose={confirmOnClose}
        >
          {ConfirmationDialogComponent && <ConfirmationDialogComponent />}
          {(messageResource && !messageResource.meta().field('shouldNotifyPlanner').attr('readonly')) && (
            <MetaControl
              attrName="shouldNotifyPlanner"
              onChange={value => onUpdateShouldNotifyPlanner(value)}
              resource={messageResource}
              fullWidth
              className={classes.notifyPlannerCheckbox}
              test="messages-notify-planner-checkbox"
            />
          )}
        </ConfirmationDialog>
      )}
      <ErrorList
        className={classNames(classes.errorList, {
          [classes.errorListNoErrors]: errors.length === 0,
        })}
        errors={errors}
      />
      {showSubject && (
        <div className={classes.subjectLine}>
          <TextField
            classes={{
              root: classes.subjectInputBase,
            }}
            label="Subject"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: subject !== undefined && subject }}
            onChange={handleSubjectChange}
            value={subject}
            test={'message-input-subject-field'}
          />
        </div>
      )}
      <div
        className={classes.inputRoot}
      >
        {!readonly
          ? pickedTextEditorComponent
          : <RichTextDisplay
            content={currentText}
          />
        }
        <MessageInputActions
          showAttachmentsButton={showAttachmentsButton}
          {...MessageInputActionsProps}
          AttachmentUploadButtonProps={{
            messageResource,
            onStart: onAttachmentUploadStart,
            onSuccess: onAttachmentUploadSuccess,
            onFailure: onAttachmentUploadFailure,
            setUploadProgress,
            uploadProgress,
            ...AttachmentUploadButtonProps,
          }}
          showAdditionalButton={showAdditionalButton}
          AdditionalButtonProps={{
            onClick: handleOnAdditionalButtonClick,
            ...AdditionalButtonProps,
          }}
          SubmitButtonProps={{
            onClick: requireConfirmation ? handleOnConfirm : handleOnSubmit,
            disabled: (!text || text === '') || disableSubmit,
            ...SubmitButtonProps,
          }}
          showTemplateOptionsMenu={showTemplateOptionsMenu}
          TemplateOptionsMenuProps={{
            onInsertTemplate: appendMessageTemplate,
            onInsertToken: editorRef.current && appendTemplateTok ? appendTemplateTok : undefined,
            templateDraftContent: text,
            ButtonProps: {
              test: 'new-thread-message-options-button',
            },
            className: classes.templateOptionsMenu,
            ...TemplateOptionsMenuProps,
          }}
        />
      </div>
      <MessageAttachments
        className={classNames(classes.attachments, {
          [classes.attachmentsNone]: attachmentResources.length === 0,
        })}
        attachmentResources={attachmentResources}
        MessageAttachmentProps={{
          onDelete: onAttachmentDelete,
        }}
      />
    </Box>
  );
};

MessageInput.defaultProps = {
  className: '',
  messageResource: null,
  errors: [],
  MessageInputActionsProps: {},
  RichTextEditorProps: {},
  SubmitButtonProps: {},
  AdditionalButtonProps: {},
  needsConfirmOnSubmit: false,
  onConfirm: noop,
  resolveConfirmation: noop,
  AttachmentUploadButtonProps: {},
  ConfirmationDialogComponent: null,
  confirmationOnSubmit: null,
  confirmationOnCancel: null,
  confirmationOnClose: null,
  showAttachmentsButton: true,
  confirmationAlertMessage: null,
  disableSubmit: false,
  readonly: false,
  showAdditionalButton: false,
  onUpdateShouldNotifyPlanner: noop,
  TemplateOptionsMenuProps: {},
  onAdditionalButtonClick: null,
  showTemplateOptionsMenu: true,
  showTemplateTokens: false,
  showSubject: false,
  setMessageRecipients: noop,
  makeCancelDefaultAction: false,
  setTemplateId: noop,
};

MessageInput.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  errors: PropTypes.array,
  messageResource: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onAdditionalButtonClick: PropTypes.func,
  MessageInputActionsProps: PropTypes.object,
  SubmitButtonProps: PropTypes.object,
  AdditionalButtonProps: PropTypes.object,
  RichTextEditorProps: PropTypes.object,
  needsConfirmOnSubmit: PropTypes.bool,
  onConfirm: PropTypes.func,
  resolveConfirmation: PropTypes.func,
  setText: PropTypes.func.isRequired,
  onUpdateShouldNotifyPlanner: PropTypes.func,
  AttachmentUploadButtonProps: PropTypes.object,
  ConfirmationDialogComponent: PropTypes.node,
  confirmationOnSubmit: PropTypes.func,
  confirmationOnCancel: PropTypes.func,
  confirmationOnClose: PropTypes.func,
  showAttachmentsButton: PropTypes.bool,
  confirmationAlertMessage: PropTypes.string,
  disableSubmit: PropTypes.bool,
  showAdditionalButton: PropTypes.bool,
  readonly: PropTypes.bool,
  TemplateOptionsMenuProps: PropTypes.object,
  showTemplateOptionsMenu: PropTypes.bool,
  showTemplateTokens: PropTypes.bool,
  showSubject: PropTypes.bool,
  setMessageRecipients: PropTypes.func,
  setTemplateId: PropTypes.func,
  makeCancelDefaultAction: PropTypes.bool,
};

export default withStyles(styles)(MessageInput);
